// ============================================================================
// Motor de FACTURACIÓN PERIÓDICA / POR DECLARACIÓN.
//
// Colectivos (vida/salud) y declarativas (transporte/incendio) no facturan por un
// plan de cuotas fijo al emitir, sino por PERÍODO: cada mes/trimestre la aseguradora
// factura, el ejecutivo la registra, se envía al cliente y entra al saco. Si el
// período pasa sin registrarse, el ejecutivo lo ve como tarea pendiente en el CRM.
//
// - `generarPeriodosPendientes`: crea los períodos que faltan (pendientes) + su tarea
//   de CRM al ejecutivo de cuenta. Idempotente (índice único poliza_id+periodo_label
//   y tarea ligada por periodo_id). Se llama al abrir el CRM.
// - `registrarFacturaPeriodo`: el ejecutivo registra el monto real → letra en `pagos`
//   (entra al saco) + cierra la tarea del período.
// ============================================================================
import { supabase, insertRow, updateRow } from './engine';
import { reaplicarSacoPoliza } from './saco';
import { aplicarPrimaEndoso } from './endosos';

const MESES = { mensual: 1, bimensual: 2, trimestral: 3, semestral: 6, anual: 12 };
export function frecMeses(frec) { return MESES[frec] || 1; }

// Fecha local YYYY-MM-DD (sin corrimiento por zona horaria).
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hoyISO = () => iso(new Date());

// Bloque de período que contiene una fecha, para un tamaño de `meses`.
// mensual → { label:'2026-07', inicio, fin }; trimestral → '2026-Q3'; etc.
export function periodoDe(fechaISO, meses) {
  const d = new Date(fechaISO + 'T12:00:00');
  const y = d.getFullYear();
  const blockStart = Math.floor(d.getMonth() / meses) * meses; // mes 0-11 de inicio del bloque
  const inicio = new Date(y, blockStart, 1);
  const fin = new Date(y, blockStart + meses, 0); // último día del bloque
  let label;
  if (meses === 1) label = `${y}-${String(blockStart + 1).padStart(2, '0')}`;
  else if (meses === 3) label = `${y}-Q${blockStart / 3 + 1}`;
  else label = `${y}-${String(blockStart + 1).padStart(2, '0')}m${meses}`;
  return { label, inicio: iso(inicio), fin: iso(fin) };
}

const MES_NOMBRE = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
// Etiqueta legible del período: '2026-07' → 'Julio 2026'; '2026-Q3' → 'Trimestre 3, 2026'.
export function etiquetaPeriodo(label) {
  if (!label) return '';
  const mm = /^(\d{4})-(\d{2})$/.exec(label);
  if (mm) return `${MES_NOMBRE[parseInt(mm[2], 10) - 1]} ${mm[1]}`;
  const mq = /^(\d{4})-Q(\d)$/.exec(label);
  if (mq) return `Trimestre ${mq[2]}, ${mq[1]}`;
  return label;
}

// Vencimiento del período: mensual = último día del mismo mes; trimestral+ = 30 días
// desde la factura (o desde el inicio del período si aún no hay fecha de factura).
export function vencimientoDe(periodo, meses, fechaFactura) {
  if (meses === 1) return periodo.fin;
  const base = new Date((fechaFactura || periodo.inicio) + 'T12:00:00');
  base.setDate(base.getDate() + 30);
  return iso(base);
}

function addMonthsISO(fechaISO, n) {
  const d = new Date(fechaISO + 'T12:00:00');
  d.setMonth(d.getMonth() + n);
  return iso(d);
}
const maxISO = (a, b) => (a > b ? a : b);

// Lista de períodos distintos entre dos fechas (inclusive), sin duplicar labels.
function periodosEntre(desdeISO, hastaISO, meses) {
  const out = [], seen = new Set();
  let cur = new Date(desdeISO + 'T12:00:00');
  const end = new Date(hastaISO + 'T12:00:00');
  let guard = 0;
  while (cur <= end && guard < 240) {
    const per = periodoDe(iso(cur), meses);
    if (!seen.has(per.label)) { seen.add(per.label); out.push(per); }
    cur.setMonth(cur.getMonth() + 1);
    guard++;
  }
  return out;
}

// Crea los períodos pendientes que falten (desde la emisión, con tope de 12 meses
// atrás para no backfillear de más) y su tarea de CRM al ejecutivo de cuenta.
// Idempotente. Devuelve { creados }.
export async function generarPeriodosPendientes(hoy = hoyISO()) {
  const { data: pols } = await supabase.from('polizas')
    .select('id, cliente_id, numero, fecha_inicio, ejecutivo_id, periodo_frecuencia, frecuencia_pago')
    .eq('facturacion_modo', 'periodica').in('estado', ['activa', 'en_renovacion']);
  if (!pols || !pols.length) return { creados: 0 };

  // ejecutivo → usuario (para asignar la tarea)
  const { data: us } = await supabase.from('usuarios')
    .select('id, ejecutivo_id').eq('activo', true).not('ejecutivo_id', 'is', null);
  const ejecToUser = {};
  (us || []).forEach(u => { ejecToUser[u.ejecutivo_id] = u.id; });

  const polIds = pols.map(p => p.id);
  const polMap = {};
  pols.forEach(p => { polMap[p.id] = p; });

  // Períodos ya existentes de TODAS las pólizas candidatas (una sola consulta, no una por póliza).
  const { data: existentesAll } = await supabase.from('poliza_periodos')
    .select('id, poliza_id, periodo_label').in('poliza_id', polIds);
  const yaHayPorPol = {};        // poliza_id -> Set(labels)
  const labelPorPeriodoId = {};  // periodo_id -> label (incluye los recién creados)
  (existentesAll || []).forEach(x => {
    (yaHayPorPol[x.poliza_id] = yaHayPorPol[x.poliza_id] || new Set()).add(x.periodo_label);
    labelPorPeriodoId[x.id] = x.periodo_label;
  });

  // Calcular los períodos que faltan de todas las pólizas (en memoria).
  const periodosNuevos = [];
  for (const p of pols) {
    const meses = frecMeses(p.periodo_frecuencia || p.frecuencia_pago);
    const desde = maxISO(p.fecha_inicio || hoy, addMonthsISO(hoy, -12));
    const periodos = periodosEntre(desde, hoy, meses);
    const yaHay = yaHayPorPol[p.id] || new Set();
    for (const per of periodos) {
      if (yaHay.has(per.label)) continue;
      periodosNuevos.push({
        poliza_id: p.id, periodo_label: per.label,
        periodo_inicio: per.inicio, periodo_fin: per.fin, estado: 'pendiente',
      });
    }
  }

  // Insertar los períodos nuevos EN BLOQUE y, con los ids devueltos, sus tareas de CRM en bloque.
  let creados = 0;
  if (periodosNuevos.length) {
    const { data: insertados } = await supabase.from('poliza_periodos').insert(periodosNuevos).select();
    creados = (insertados || []).length;
    const tareas = (insertados || []).map(np => {
      const p = polMap[np.poliza_id];
      const etiqueta = etiquetaPeriodo(np.periodo_label);
      labelPorPeriodoId[np.id] = np.periodo_label;
      return {
        tipo: 'declaracion',
        titulo: `Factura de ${etiqueta} — póliza ${p?.numero || np.poliza_id}`,
        descripcion: `Pedir/descargar de la aseguradora la factura de ${etiqueta} y registrarla en el sistema para enviarla al cliente.`,
        poliza_id: np.poliza_id, cliente_id: p?.cliente_id || null,
        asignado_a: p?.ejecutivo_id ? (ejecToUser[p.ejecutivo_id] || null) : null,
        estado: 'abierto', prioridad: 'normal',
        fecha_vencimiento: np.periodo_fin, periodo_id: np.id,
      };
    });
    if (tareas.length) await supabase.from('crm_gestiones').insert(tareas);
  }

  // Auto-corrige el título/descripción de tareas ANTIGUAS (creadas antes del formato
  // legible "Julio 2026"): una sola consulta para todas + updates en paralelo.
  const { data: tareasViejas } = await supabase.from('crm_gestiones')
    .select('id, titulo, descripcion, periodo_id, poliza_id')
    .in('poliza_id', polIds).eq('tipo', 'declaracion').eq('estado', 'abierto');
  const fixes = [];
  for (const t of (tareasViejas || [])) {
    const label = labelPorPeriodoId[t.periodo_id];
    if (!label) continue;
    const p = polMap[t.poliza_id];
    const etiqueta = etiquetaPeriodo(label);
    const nuevoTitulo = `Factura de ${etiqueta} — póliza ${p?.numero || t.poliza_id}`;
    const nuevaDesc = `Pedir/descargar de la aseguradora la factura de ${etiqueta} y registrarla en el sistema para enviarla al cliente.`;
    if (t.titulo !== nuevoTitulo || t.descripcion !== nuevaDesc) {
      fixes.push(updateRow('crm_gestiones', t.id, { titulo: nuevoTitulo, descripcion: nuevaDesc }));
    }
  }
  if (fixes.length) await Promise.all(fixes);

  return { creados };
}

// Prefijo del título del recordatorio de regularización (para deduplicar y cerrar).
export const TITULO_REGULARIZACION = 'Regularización de cierre';

// DECLARATIVAS ACUMULATIVAS — recordatorio ANUAL de la regularización de cierre.
// Al acercarse/llegar el vencimiento de una póliza declarativa ACUMULATIVA con prima
// depósito, crea UNA tarea de CRM al ejecutivo de cuenta recordando generar la
// regularización de cierre (comparar depósito vs. total declarado). NO mueve dinero:
// la persona revisa y la genera con un clic (pestaña Cierre). Idempotente: no duplica
// si ya hay una tarea abierta o si la regularización ya se generó.
export async function generarRecordatoriosRegularizacion(hoy = hoyISO()) {
  const limite = addMonthsISO(hoy, 0); // base; se amplía 15 días abajo
  const hasta = (() => { const d = new Date(limite + 'T12:00:00'); d.setDate(d.getDate() + 15); return iso(d); })();

  const { data: pols } = await supabase.from('polizas')
    .select('id, numero, cliente_id, ejecutivo_id, fecha_vencimiento, prima_deposito')
    .eq('es_declarativa', true).eq('declarativa_modo', 'acumulativo')
    .in('estado', ['activa', 'en_renovacion'])
    .gt('prima_deposito', 0)
    .lte('fecha_vencimiento', hasta);
  if (!pols || !pols.length) return { creados: 0 };
  const polIds = pols.map(p => p.id);

  // ejecutivo → usuario (para asignar la tarea)
  const { data: us } = await supabase.from('usuarios')
    .select('id, ejecutivo_id').eq('activo', true).not('ejecutivo_id', 'is', null);
  const ejecToUser = {};
  (us || []).forEach(u => { ejecToUser[u.ejecutivo_id] = u.id; });

  // Ya regularizadas (una query) → se saltan.
  const { data: regs } = await supabase.from('poliza_regularizaciones').select('poliza_id').in('poliza_id', polIds);
  const yaRegularizada = new Set((regs || []).map(r => r.poliza_id));

  // Tareas de recordatorio ya abiertas (una query) → no duplicar.
  const { data: tareas } = await supabase.from('crm_gestiones')
    .select('poliza_id, titulo').in('poliza_id', polIds).eq('tipo', 'seguimiento').in('estado', ['abierto', 'en_proceso']);
  const conTarea = new Set((tareas || []).filter(t => (t.titulo || '').startsWith(TITULO_REGULARIZACION)).map(t => t.poliza_id));

  const nuevas = [];
  for (const p of pols) {
    if (yaRegularizada.has(p.id) || conTarea.has(p.id)) continue;
    nuevas.push({
      tipo: 'seguimiento', prioridad: 'alta',
      titulo: `${TITULO_REGULARIZACION} — Póliza ${p.numero || p.id}`,
      descripcion: `La póliza declarativa acumulativa ${p.numero || ''} vence el ${p.fecha_vencimiento}. Genera la regularización de cierre (pestaña Cierre de la póliza): compara la prima depósito con el total declarado y aplica el cobro de exceso o la devolución.`,
      poliza_id: p.id, cliente_id: p.cliente_id,
      asignado_a: p.ejecutivo_id ? (ejecToUser[p.ejecutivo_id] || null) : null,
      estado: 'abierto', fecha_vencimiento: p.fecha_vencimiento,
    });
  }
  if (nuevas.length) await supabase.from('crm_gestiones').insert(nuevas);
  return { creados: nuevas.length };
}

// DECLARATIVAS (modelo "una letra por declaración"): cuando se registra una declaración
// (endoso) para un mes, ese período ya quedó DECLARADO. Marca el período como facturado
// y cierra su recordatorio en el CRM. NO bloquea el mes: pueden entrar más declaraciones
// después (cada una su propia letra). Idempotente y tolerante a que aún no exista el período.
export async function cerrarPeriodoDeclarado(polizaId, fechaISO) {
  if (!polizaId || !fechaISO) return;
  const { data: pol } = await supabase.from('polizas')
    .select('periodo_frecuencia, frecuencia_pago').eq('id', polizaId).maybeSingle();
  const meses = frecMeses(pol?.periodo_frecuencia || pol?.frecuencia_pago);
  const per = periodoDe(fechaISO, meses);
  const { data: existente } = await supabase.from('poliza_periodos')
    .select('id, estado').eq('poliza_id', polizaId).eq('periodo_label', per.label).maybeSingle();
  if (!existente) return; // el período se crea al abrir el CRM; si no existe, no hay recordatorio que cerrar
  if (existente.estado !== 'facturado') {
    await updateRow('poliza_periodos', existente.id, { estado: 'facturado', fecha_factura: fechaISO });
  }
  await supabase.from('crm_gestiones')
    .update({ estado: 'cerrado', actualizado_en: new Date().toISOString() })
    .eq('periodo_id', existente.id).in('estado', ['abierto', 'en_proceso']);
}

// DECLARATIVAS — registrar una DECLARACIÓN directamente desde la póliza (pestaña
// Declaraciones), sin salir a Solicitudes. La declaración ES un endoso: se crea un
// `poliza_endosos` (interno) y su PROPIA letra vía `aplicarPrimaEndoso` (en declarativas
// no hay cuotas fijas → sale una letra única con vencimiento en la fecha declarada).
// Marca/crea el período del mes y cierra su recordatorio en el CRM. Devuelve
// { endoso, periodoId } para poder ligar los documentos de respaldo (factura, etc.).
export async function registrarDeclaracion(poliza, opts = {}) {
  const monto = parseFloat(opts.monto);
  if (!poliza?.id) throw new Error('Póliza inválida');
  if (!(monto > 0)) throw new Error('El monto declarado debe ser mayor a 0.');
  const fecha = opts.fecha || hoyISO();
  const imp = poliza.impuesto == null ? 5 : poliza.impuesto;
  const meses = frecMeses(poliza.periodo_frecuencia || poliza.frecuencia_pago);
  const per = periodoDe(fecha, meses);

  // Asegurar que exista el período del mes (para ligar los documentos y mostrar la
  // declaración agrupada). Si no existe (el CRM aún no lo generó), se crea aquí.
  let { data: prow } = await supabase.from('poliza_periodos')
    .select('id').eq('poliza_id', poliza.id).eq('periodo_label', per.label).maybeSingle();
  if (!prow) {
    prow = await insertRow('poliza_periodos', {
      poliza_id: poliza.id, periodo_label: per.label,
      periodo_inicio: per.inicio, periodo_fin: per.fin, estado: 'pendiente',
    });
  }

  // La declaración = endoso interno con su letra propia.
  const endoso = await insertRow('poliza_endosos', {
    poliza_id: poliza.id,
    fecha,
    descripcion: opts.descripcion?.trim() || `Declaración ${etiquetaPeriodo(per.label)}`,
    numero_endoso: opts.numeroEndoso || null,
    delta_prima: monto,
    es_interno: true,
    estado: 'activo',
  });
  // Letra de la declaración (con impuesto) + reaplica saco. Sin cuotas fijas → letra única.
  await aplicarPrimaEndoso(poliza.id, endoso.id, monto, {
    impuesto: imp, fechaDesde: fecha, formaPago: poliza.forma_pago || null,
  });
  // Cierra el recordatorio del mes en el CRM y marca el período declarado/facturado.
  try { await cerrarPeriodoDeclarado(poliza.id, fecha); } catch { /* no crítico */ }

  return { endoso, periodoId: prow?.id || null };
}

// El ejecutivo registra la factura real del período: setea el monto, crea la letra
// en `pagos` (entra al saco) y cierra la tarea del CRM del período.
export async function registrarFacturaPeriodo(periodo, opts = {}) {
  const m = parseFloat(opts.monto);
  if (!periodo || !periodo.id) throw new Error('Período inválido');
  if (!(m >= 0)) throw new Error('Monto inválido');

  const { data: pol } = await supabase.from('polizas')
    .select('id, periodo_frecuencia, frecuencia_pago, forma_pago')
    .eq('id', periodo.poliza_id).maybeSingle();
  const meses = frecMeses(pol?.periodo_frecuencia || pol?.frecuencia_pago);
  const fechaFactura = opts.fechaFactura || hoyISO();
  const venc = opts.fechaVencimiento
    || vencimientoDe({ inicio: periodo.periodo_inicio, fin: periodo.periodo_fin }, meses, fechaFactura);

  await updateRow('poliza_periodos', periodo.id, {
    estado: 'facturado', monto: m,
    fecha_factura: fechaFactura,
    fecha_envio_cliente: opts.fechaEnvioCliente || null,
    fecha_vencimiento: venc,
    notas: opts.notas || null,
  });

  await insertRow('pagos', {
    poliza_id: periodo.poliza_id, monto: m, estado: 'pendiente',
    fecha_vencimiento: venc, metodo: pol?.forma_pago || opts.formaPago || null,
    cuota_num: null, origen: 'periodo',
  });

  await reaplicarSacoPoliza(periodo.poliza_id);

  // Cierra la tarea del CRM ligada a este período.
  await supabase.from('crm_gestiones')
    .update({ estado: 'cerrado', actualizado_en: new Date().toISOString() })
    .eq('periodo_id', periodo.id).eq('estado', 'abierto');
}
